import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { ArrowRight } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { Expansion } from '../../types';
import { cn } from '../../utils';

type Props = {
    href: string;
    image?: string | null;
    release: Expansion;
    releaseDate: string | null;
};

export const GameReleaseFeature: FC<Props> = ({
    href,
    image,
    release,
    releaseDate,
}) => (
    <article
        className={cn(
            'relative isolate flex aspect-[2.05/1] min-h-0 overflow-hidden rounded-3xl border bg-black sm:aspect-[2.25/1] lg:aspect-auto lg:h-full',
            !image && 'bg-gradient-to-br from-primary/25 via-card to-primary/5',
        )}
        data-testid="game-release-feature"
    >
        {image ? (
            <img
                key={image}
                src={image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-[center_22%]"
            />
        ) : null}
        <div className="absolute inset-0 hidden bg-gradient-to-r from-black from-0% via-black via-40% to-transparent to-75% lg:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent lg:from-black/70 lg:via-transparent" />

        <div className="relative z-10 mt-auto flex w-full items-end justify-between gap-3 p-4 text-white sm:p-5 lg:block lg:max-w-3xl lg:p-12">
            <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-white/75 lg:mb-4 lg:gap-3 lg:text-sm">
                    {release.tag ? (
                        <>
                            <span className="font-semibold tracking-[0.12em] text-primary uppercase lg:hidden">
                                {release.tag}
                            </span>
                            <span className="hidden rounded-full border border-white/30 bg-black/20 px-3 py-1 font-semibold tracking-[0.14em] uppercase backdrop-blur-sm lg:inline-flex">
                                {release.tag}
                            </span>
                        </>
                    ) : null}
                    {releaseDate ? (
                        <span className="hidden lg:inline">{releaseDate}</span>
                    ) : null}
                    {(release.products_count ?? 0) > 0 ? (
                        <span className="hidden lg:inline">
                            {release.products_count} карток
                        </span>
                    ) : null}
                </div>

                <h2 className="line-clamp-2 max-w-2xl text-xl leading-tight font-bold tracking-tight sm:text-3xl lg:text-6xl lg:leading-[1.05]">
                    {release.name}
                </h2>
            </div>

            <div className="shrink-0 lg:mt-7">
                <Button
                    asChild
                    size="lg"
                    className="h-12 rounded-2xl px-4 text-sm shadow-lg sm:h-14 sm:px-6 sm:text-base lg:h-11"
                >
                    <Link
                        href={href}
                        data-testid="selected-release-singles-link"
                    >
                        <span className="lg:hidden">Дивитись</span>
                        <span className="hidden lg:inline">Сингли сету</span>
                        <ArrowRight className="ml-1.5 h-4 w-4 lg:ml-2" />
                    </Link>
                </Button>
            </div>
        </div>
    </article>
);
